import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

const Pong = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState({ player: 0, computer: 0 });
  const gameStateRef = useRef({
    ball: { x: 400, y: 300, dx: 5, dy: 5, radius: 8 },
    playerPaddle: { x: 10, y: 250, width: 10, height: 100 },
    computerPaddle: { x: 780, y: 250, width: 10, height: 100 },
    playerScore: 0,
    computerScore: 0,
    gameOver: false
  });

  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 600;

  const initGame = () => {
    gameStateRef.current = {
      ball: { x: 400, y: 300, dx: 5, dy: 5, radius: 8 },
      playerPaddle: { x: 10, y: 250, width: 10, height: 100 },
      computerPaddle: { x: 780, y: 250, width: 10, height: 100 },
      playerScore: 0,
      computerScore: 0,
      gameOver: false
    };
    setScore({ player: 0, computer: 0 });
    setGameState('playing');
  };

  const draw = (ctx) => {
    const game = gameStateRef.current;

    // Sfondo
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Linea centrale
    ctx.strokeStyle = '#fff';
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(CANVAS_WIDTH / 2, 0);
    ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
    ctx.stroke();
    ctx.setLineDash([]);

    // Palla
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(game.ball.x, game.ball.y, game.ball.radius, 0, Math.PI * 2);
    ctx.fill();

    // Paddle del giocatore
    ctx.fillRect(
      game.playerPaddle.x,
      game.playerPaddle.y,
      game.playerPaddle.width,
      game.playerPaddle.height
    );

    // Paddle del computer
    ctx.fillRect(
      game.computerPaddle.x,
      game.computerPaddle.y,
      game.computerPaddle.width,
      game.computerPaddle.height
    );
  };

  const updateGame = () => {
    const game = gameStateRef.current;

    // Muovi la palla
    game.ball.x += game.ball.dx;
    game.ball.y += game.ball.dy;

    // Rimbalzo verticale
    if (game.ball.y - game.ball.radius < 0 || game.ball.y + game.ball.radius > CANVAS_HEIGHT) {
      game.ball.dy *= -1;
    }

    // Collisione con i paddle
    if (
      game.ball.x - game.ball.radius < game.playerPaddle.x + game.playerPaddle.width &&
      game.ball.y > game.playerPaddle.y &&
      game.ball.y < game.playerPaddle.y + game.playerPaddle.height
    ) {
      game.ball.dx *= -1;
    }

    if (
      game.ball.x + game.ball.radius > game.computerPaddle.x &&
      game.ball.y > game.computerPaddle.y &&
      game.ball.y < game.computerPaddle.y + game.computerPaddle.height
    ) {
      game.ball.dx *= -1;
    }

    // Movimento del computer
    const computerCenter = game.computerPaddle.y + game.computerPaddle.height / 2;
    if (computerCenter < game.ball.y - 35) {
      game.computerPaddle.y += 5;
    } else if (computerCenter > game.ball.y + 35) {
      game.computerPaddle.y -= 5;
    }

    // Vincita
    if (game.ball.x < 0) {
      game.computerScore++;
      setScore({ ...score, computer: game.computerScore });
      game.ball = { x: 400, y: 300, dx: 5, dy: 5, radius: 8 };
    }
    if (game.ball.x > CANVAS_WIDTH) {
      game.playerScore++;
      setScore({ ...score, player: game.playerScore });
      game.ball = { x: 400, y: 300, dx: 5, dy: 5, radius: 8 };
    }
  };

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const gameLoop = setInterval(() => {
      updateGame();
      draw(ctx);
    }, 30);

    return () => clearInterval(gameLoop);
  }, [gameState, score]);

  // Disegna iniziale
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (gameState === 'menu') {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      ctx.fillStyle = '#fff';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Pong Game', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 30);
      ctx.font = '16px Arial';
      ctx.fillText('Premi Play per iniziare', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 20);
    } else {
      draw(ctx);
    }
  }, [gameState]);

  // Keyboard controls
  useEffect(() => {
    const handleMouseMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const y = e.clientY - rect.top;

      gameStateRef.current.playerPaddle.y = Math.max(
        0,
        Math.min(y - gameStateRef.current.playerPaddle.height / 2, CANVAS_HEIGHT - gameStateRef.current.playerPaddle.height)
      );
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-lg">
            <div className="card-header bg-info text-white">
              <h3 className="mb-0">🏓 Pong Game</h3>
            </div>
            <div className="card-body text-center">
              <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                style={{
                  border: '2px solid #333',
                  borderRadius: '8px',
                  backgroundColor: '#000',
                  display: 'block',
                  margin: '0 auto 1rem'
                }}
              />

              <div className="row mb-3">
                <div className="col-6">
                  <h5>Tu</h5>
                  <h2 className="text-success">{score.player}</h2>
                </div>
                <div className="col-6">
                  <h5>Computer</h5>
                  <h2 className="text-danger">{score.computer}</h2>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-center">
                {gameState === 'menu' && (
                  <button onClick={initGame} className="btn btn-success btn-lg">
                    <Play size={20} className="me-2" />
                    Inizia
                  </button>
                )}
                {gameState === 'playing' && (
                  <button onClick={() => setGameState('paused')} className="btn btn-warning btn-lg">
                    <Pause size={20} className="me-2" />
                    Pausa
                  </button>
                )}
                {gameState === 'paused' && (
                  <button onClick={() => setGameState('playing')} className="btn btn-success btn-lg">
                    <Play size={20} className="me-2" />
                    Riprendi
                  </button>
                )}
              </div>

              <div className="alert alert-info mt-3">
                <strong>Come Giocare:</strong> Muovi il mouse su e giù per controllare il paddle!
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pong;
